/**
 * @fileoverview The Edit Client form (PATCH /sales/clients/{tenantId}).
 * @description The five sections of Edit Prospect, filled with the client's current values, except
 * that the bank type and the registration number can change here. Changing either asks for a
 * confirmation first, since it is the bank's identity. The form saves against the version it was
 * opened with; a change made by someone else since (a newer ETag, or a `412 / 4105` refusal) shows
 * the stale-version notice and pauses saving, and what was typed stays until the person's Refresh.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { tierNameFor } from '@/features/sales/lib/prospect-form.utils'
import {
  changesBankIdentity,
  isStaleClientVersion,
  toEditClientRequest,
} from '@/features/sales/lib/sales-clients.utils'
import {
  editClientFormSchema,
  type EditClientFormValues,
} from '@/features/sales/schemas/edit-client-form.schema'

import DialogActions from './DialogActions'
import EditProspectDetailsFields from './EditProspectDetailsFields'
import SalesRequestError from './SalesRequestError'
import StaleVersionNotice from './StaleVersionNotice'

import type { useEditClient } from '@/features/sales/hooks/useEditClient'
import type { ProspectMetadata } from '@/features/sales/types/prospect.types'
import type { ClientDetails } from '@/features/sales/types/sales-client.types'
import type { FormEvent } from 'react'

interface EditClientFormProps {
  client: ClientDetails
  etag: string
  startValues: EditClientFormValues
  metadata: ProspectMetadata
  edit: ReturnType<typeof useEditClient>
  onClose: () => void
  onRefresh: () => void
  isRefreshing: boolean
  /** Whether a refetch has returned a newer version than the form was opened with. */
  changedSince: boolean
}

/**
 * @component
 * @param {EditClientFormProps} props - The loaded client, its version and the handlers.
 * @param {ClientDetails} props.client - The client the form was opened with.
 * @param {string} props.etag - The version of those details, sent as If-Match.
 * @param {EditClientFormValues} props.startValues - The form's starting values.
 * @param {ProspectMetadata} props.metadata - The catalogue choices.
 * @param {ReturnType<typeof useEditClient>} props.edit - The dialog's save mutation.
 * @param {() => void} props.onClose - Dismisses the dialog (held while the save is in flight).
 * @param {() => void} props.onRefresh - Loads the latest version over what was typed.
 * @param {boolean} props.isRefreshing - Whether that reload is running.
 * @param {boolean} props.changedSince - Whether a newer version has been fetched.
 */
function EditClientForm({
  client,
  etag,
  startValues,
  metadata,
  edit,
  onClose,
  onRefresh,
  isRefreshing,
  changedSince,
}: EditClientFormProps) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState<EditClientFormValues | null>(null)
  const refused = edit.isError && isStaleClientVersion(edit.error)
  const isStale = refused || changedSince
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EditClientFormValues>({
    resolver: zodResolver(editClientFormSchema),
    defaultValues: startValues,
  })
  const branches = useWatch({ control, name: 'branches' })

  /**
   * @description Saves the values against the version shown; the dialog's mutation reports it.
   * @param {EditClientFormValues} values - The validated form values.
   */
  const save = (values: EditClientFormValues) => {
    edit.mutate({
      tenantId: client.tenantId,
      etag,
      request: toEditClientRequest(values, client),
    })
  }

  /**
   * @description Asks for a confirmation when the bank's identity changed, else saves.
   * @param {EditClientFormValues} values - The validated form values.
   */
  const onValid = (values: EditClientFormValues) => {
    if (changesBankIdentity(values, client)) setConfirming(values)
    else save(values)
  }

  /**
   * @description Validates and submits the form.
   * @param {FormEvent<HTMLFormElement>} event - The submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  /**
   * @description Clears a stale refusal and loads the client's latest values over the form.
   */
  const refresh = () => {
    edit.reset()
    setConfirming(null)
    onRefresh()
  }

  if (confirming) {
    return (
      <div className="flex flex-col gap-4" data-testid="edit-client-confirm">
        <p className="text-compact-lg text-ink" data-testid="edit-client-confirm-message">
          {t('sales.activeClients.edit.confirmIdentity', { bank: client.bankName })}
        </p>
        {edit.isError && !refused && <SalesRequestError error={edit.error} />}
        {isStale && (
          <StaleVersionNotice
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            message={refused ? undefined : t('sales.activeClients.edit.changedSince')}
          />
        )}
        <DialogActions
          testIdPrefix="edit-client-confirm"
          cancelLabel={t('sales.activeClients.edit.back')}
          confirmLabel={t('sales.activeClients.edit.confirm')}
          onCancel={() => setConfirming(null)}
          onConfirm={() => save(confirming)}
          isLoading={edit.isPending}
          disabled={isStale}
        />
      </div>
    )
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      data-testid="edit-client-form"
    >
      <EditProspectDetailsFields
        register={register}
        errors={errors}
        metadata={metadata}
        previewTier={tierNameFor(metadata.tierScheme.ranges, branches || 0)}
        identityEditable
        testIdPrefix="edit-client"
      />
      <div className="col-span-full flex flex-col gap-3">
        {isStale && (
          <StaleVersionNotice
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            message={refused ? undefined : t('sales.activeClients.edit.changedSince')}
          />
        )}
        {edit.isError && !refused && <SalesRequestError error={edit.error} />}
        <DialogActions
          testIdPrefix="edit-client"
          cancelLabel={t('sales.pipeline.form.cancel')}
          confirmLabel={t('sales.pipeline.edit.submit')}
          onCancel={onClose}
          confirmType="submit"
          isLoading={edit.isPending}
          disabled={isStale}
        />
      </div>
    </form>
  )
}

export default EditClientForm
